import { Box, Checkbox, useToast } from '@chakra-ui/react'
import ControlInput from '../components/ControlInput'
import ConfirmButton from '../components/ConfirmButton'
import { SubmitHandler, useForm } from 'react-hook-form'
import ControlSelect from '../components/ControlSelect'
import ControlDataPicker from '../components/ControlDataPicker'
import { NewPatient } from '../../../utils/schema'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  useCreatePatient,
  useEditPatient,
  useGetPatient,
  usePatientsList,
} from '../../../api/api_hooks/patients'
import { useEffect } from 'react'
import AddressInput from '../components/AddressInput'
import { useTranslation } from 'react-i18next'

type Inputs = {
  firstName: string
  lastName: string
  birthName?: string | null
  dateOfBirth: Date
  gender: string
  address: string
  socialSecurityNumber?: string | null
  twinRank?: number | null
  phoneNumber?: string | null
  confirmedPatientConsent: boolean
}

interface PatientFormProps {
  id?: string
  onClose?: (patient?: any) => void
  noContainer?: boolean
}

export default function PatientForm({ id, onClose, noContainer = false }: PatientFormProps) {
  const toast = useToast()
  const { t } = useTranslation()

  const {
    mutate: mutateEditPatient,
    isSuccess: isSuccessEditPatient,
    data: patchData,
  } = useEditPatient()
  const { mutate, isSuccess, data, isError } = useCreatePatient()
  const { refetch } = usePatientsList()
  const { data: patientData, refetch: getPatientAgain } = useGetPatient(id)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    control,
  } = useForm<Inputs>({
    resolver: yupResolver(NewPatient),
  })
  const onSubmit: SubmitHandler<Inputs> = (data) => {
    patientData
      ? mutateEditPatient({ id: patientData.id, data })
      : mutate(data as unknown as void)
  }

  useEffect(() => {
    if (patientData) {
      setValue('gender', patientData.gender)
      setValue('dateOfBirth', new Date(Date.parse(patientData.dateOfBirth)))
      setValue('address', patientData.address)
      setValue('socialSecurityNumber', patientData.socialSecurityNumber ?? null)
      setValue('twinRank', patientData.twinRank)
      setValue('phoneNumber', patientData.phoneNumber)
      setValue('firstName', patientData.firstName)
      setValue('lastName', patientData.lastName)
      setValue('birthName', patientData.birthName)
      setValue('confirmedPatientConsent', patientData.confirmedPatientConsent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData])

  useEffect(() => {
    if (isSuccess || isSuccessEditPatient) {
      refetch()
      getPatientAgain()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isSuccessEditPatient])

  useEffect(() => {
    if (data || patchData) {
      toast({
        title: `${id ? t('shared.common.updatedWithSuccess') : t('shared.common.createdWithSuccess')}`,
        position: 'top',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      // Pass the created/updated patient data to the callback
      const patientData = data?.data || patchData?.data
      console.log('📋 PatientForm - Full response data:', data)
      console.log('📋 PatientForm - Full response patchData:', patchData)
      console.log('📋 PatientForm - Extracted patient data:', patientData)
      console.log('📋 PatientForm - Patient ID:', patientData?.id)
      onClose?.(patientData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, patchData])

  if (isError) {
    toast({
      title: t('shared.errors.errorMsg'),
      position: 'top',
      status: 'error',
      duration: 5000,
      isClosable: true,
    })
  }

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" gap={4} mb={4}>
        <ControlInput
          register={{ ...register('firstName') }}
          label={t('shared.common.firstNameLabel')}
          placeholder={t('shared.common.firstNamePlaceholder')}
          isInvalid={!!errors.firstName}
          errorMessage={errors.firstName && errors.firstName.message}
        />
        <ControlInput
          register={{ ...register('lastName') }}
          label={t('shared.common.lastNameLabel')}
          placeholder={t('shared.common.lastNamePlaceholder')}
          isInvalid={!!errors.lastName}
          errorMessage={errors.lastName && errors.lastName.message}
        />
      </Box>
      <Box display="flex" gap={4} mb={4}>
        <ControlInput
          register={{ ...register('birthName') }}
          label={`${t('shared.common.birthNameLabel')} (${t('shared.common.optional')})`}
          placeholder={t('shared.common.birthNamePlaceholder')}
          isInvalid={!!errors.birthName}
          errorMessage={errors.birthName && errors.birthName.message}
        />
        <ControlDataPicker
          register={{ ...register('dateOfBirth') }}
          label={t('shared.common.dateOfBirthLabel')}
          placeholder={t('shared.common.dateOfBirthPlaceholder')}
          isInvalid={!!errors.dateOfBirth}
          errorMessage={errors.dateOfBirth && errors.dateOfBirth.message}
          control={control}
        />
      </Box>
      <ControlInput
        register={{ ...register('socialSecurityNumber') }}
        label={t('patient.socialSecurityNumberLabel')}
        placeholder={t('patient.socialSecurityNumberPlaceholder')}
        isInvalid={!!errors.socialSecurityNumber}
        errorMessage={
          errors.socialSecurityNumber && errors.socialSecurityNumber.message
        }
      />
      <Box mt={4}>
        <AddressInput
          onAddressSelect={(address) => {
            setValue('address', address)
          }}
          label={t('shared.common.fullAddressLabel')}
          inputData={patientData?.address}
          isInvalid={!!errors.address}
          errorMessage={errors.address && errors.address.message}
          fullAddressOnly={true}
        />
      </Box>
      <ControlInput
        register={{ ...register('phoneNumber') }}
        label={`${t('shared.common.phoneLabel')} (${t('shared.common.optional')})`}
        placeholder={t('shared.common.phonePlaceholder')}
        isInvalid={!!errors.phoneNumber}
        errorMessage={errors.phoneNumber && errors.phoneNumber.message}
      />
      <Box display="flex" gap={4} mb={4}>
        <ControlSelect
          register={{ ...register('twinRank') }}
          label={`${t('patient.twinRankLabel')} (${t('shared.common.optional')})`}
          placeholder={t('patient.twinRankPlaceholder')}
          isInvalid={!!errors.twinRank}
          errorMessage={errors.twinRank && errors.twinRank.message}
          options={['1', '2', '3', '4']}
        />
        <ControlSelect
          register={{ ...register('gender') }}
          label={t('shared.common.genderLabel')}
          placeholder={t('shared.common.genderPlaceholder')}
          isInvalid={!!errors.gender}
          errorMessage={errors.gender && errors.gender.message}
          options={['male', 'female']}
        />
      </Box>
      <Box display="flex" gap={4} mt={8}>
        <Box display="flex" alignItems="center" w="60%">
          <Checkbox
            {...register('confirmedPatientConsent')}
            id="confirmedPatientConsent"
            colorScheme="green"
          >
            {t('patient.confirmedPatientConsentMsg')}
          </Checkbox>
          {errors.confirmedPatientConsent && (
            <Box color="red.500" fontSize="sm" ml={2}>
              {errors.confirmedPatientConsent.message}
            </Box>
          )}
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="40%"
        >
          <ConfirmButton
            text={`${id ? t('shared.common.update') : t('shared.common.create')}`}
            width="100%"
          />
        </Box>
      </Box>
    </form>
  )

  if (noContainer) {
    return formContent
  }

  return (
    <Box
      minW={270}
      bg="white"
      borderRadius="xl"
      p={6}
      boxShadow="sm"
    >
      {formContent}
    </Box>
  )
}
